import type { LocalPublicationReceipt } from '@streamlain/doc-publication-contract';
import type { DatabaseImportMeasurement } from '@streamlain/doc-publication-contract/database-writer';
import {
  DOC_SYNC_FAILURE_CLASSES,
  type DocSyncFailureClass,
} from '@streamlain/doc-sync-taxonomy';

export const DATABASE_IMPORT_PROGRESS_MARK = 'sl-db-import-progress';
export const DATABASE_IMPORT_FAILURE_MARK = 'sl-db-import-failure';
export const DATABASE_IMPORT_PACKET_MARK = 'sl-db-import-packet';
export const DATABASE_IMPORT_APPLIED_MARK = 'sl-db-import-applied';
const ROW_MARK_LIMIT = 1024;
const FAILURE_SAMPLE_LIMIT = 16;

export type DatabaseImportSyncState =
  | 'reading'
  | 'saved-local'
  | 'sync-pending'
  | 'synced'
  | 'conflict'
  | 'cancelled';

export type DatabaseImportSyncSnapshot = {
  readRows: number;
  localCommittedRows: number;
  remoteCommittedRows: number;
  state: DatabaseImportSyncState;
};

/** Allowlisted failure evidence and bounded worker error details. */
export type DatabaseImportFailureSample = {
  phase: 'import' | 'sync';
  code: string | null;
  failureClass: DocSyncFailureClass | null;
  failureStage?: string;
  failureName?: string;
  failureMessage?: string;
  operationId: string | null;
  timestamp: number;
};

const CONFLICT_CODES = new Set([
  'FORBIDDEN',
  'FORMAT_MISMATCH',
  'LIMIT_EXCEEDED',
  'REMOTE_CONFLICT',
]);

function readField(error: unknown, key: string): unknown {
  try {
    return typeof error === 'object' && error !== null
      ? (error as Record<string, unknown>)[key]
      : undefined;
  } catch {
    return undefined;
  }
}

function allowlisted(value: unknown, pattern: RegExp): string | null {
  return typeof value === 'string' && pattern.test(value) ? value : null;
}

/**
 * Internal import progress, derived from the local commits and the remote
 * receipts of this import only. It never waits, never renders text and never
 * changes the import result; marks are read by the measurement harness.
 */
export class DatabaseImportSyncProgress {
  private readonly snapshot: DatabaseImportSyncSnapshot = {
    readRows: 0,
    localCommittedRows: 0,
    remoteCommittedRows: 0,
    state: 'reading',
  };
  private readonly samples: DatabaseImportFailureSample[] = [];
  private readonly receipts: LocalPublicationReceipt[] = [];
  private readonly syncedDocIds = new Set<string>();
  private rowMarks = 0;
  private packetMarks = 0;
  private appliedMarks = 0;

  get current(): DatabaseImportSyncSnapshot {
    return { ...this.snapshot };
  }

  get failures(): readonly DatabaseImportFailureSample[] {
    return [...this.samples];
  }

  /** Rows read from the source and rows committed to the local store. */
  local(readRows: number, localCommittedRows: number): void {
    if (this.snapshot.state !== 'reading') return;
    const read = Math.max(this.snapshot.readRows, readRows, localCommittedRows);
    const committed = Math.max(
      this.snapshot.localCommittedRows,
      localCommittedRows
    );
    if (
      read === this.snapshot.readRows &&
      committed === this.snapshot.localCommittedRows
    )
      return;
    this.snapshot.readRows = read;
    this.snapshot.localCommittedRows = committed;
    this.mark(false);
  }

  /** Worker packet timings only; no row payloads or identifiers. */
  packets(measurements: readonly DatabaseImportMeasurement[]): void {
    for (const measurement of measurements) {
      if (this.packetMarks++ >= ROW_MARK_LIMIT) return;
      this.markNumbers(DATABASE_IMPORT_PACKET_MARK, {
        startedUnixMs: measurement.startedUnixMs,
        cumulativeMs: measurement.cumulativeMs,
        from: measurement.from,
        to: measurement.to,
        batchMs: measurement.batchMs,
        normalizeMs: measurement.normalizeMs,
        archiveMs: measurement.archiveMs,
        archiveBytes: measurement.archiveBytes,
        'writer.readMs': measurement.writer.readMs,
        'writer.computeMs': measurement.writer.computeMs,
        'writer.encodeMs': measurement.writer.encodeMs,
        'writer.commitMs': measurement.writer.commitMs,
        'writer.totalMs': measurement.writer.totalMs,
      });
    }
  }

  applied(ackApplyMs: number): void {
    if (this.appliedMarks++ >= ROW_MARK_LIMIT) return;
    this.markNumbers(DATABASE_IMPORT_APPLIED_MARK, { ackApplyMs });
  }

  /** Every row is committed locally; the import is complete offline. */
  savedLocally(rows: number): void {
    if (this.snapshot.state !== 'reading') return;
    this.snapshot.readRows = Math.max(this.snapshot.readRows, rows);
    this.snapshot.localCommittedRows = Math.max(
      this.snapshot.localCommittedRows,
      rows
    );
    this.snapshot.state = 'saved-local';
    this.mark(true);
  }

  /** Remote delivery of exactly these receipts has started. */
  syncing(receipts: readonly LocalPublicationReceipt[]): void {
    if (this.snapshot.state !== 'saved-local') return;
    this.receipts.push(...receipts);
    this.snapshot.state = 'sync-pending';
    this.mark(true);
  }

  /** One document of the receipts is confirmed with its committed rows. */
  remote(docId: string, rows: number): void {
    if (this.snapshot.state !== 'sync-pending') return;
    this.syncedDocIds.add(docId);
    const remote = Math.min(
      this.snapshot.localCommittedRows,
      this.snapshot.remoteCommittedRows + Math.max(0, rows)
    );
    if (remote === this.snapshot.remoteCommittedRows) return;
    this.snapshot.remoteCommittedRows = remote;
    this.mark(false);
  }

  /** The server confirmed the import document and its access bindings. */
  synced(): void {
    if (this.snapshot.state !== 'sync-pending') return;
    this.snapshot.remoteCommittedRows = this.snapshot.localCommittedRows;
    this.snapshot.state = 'synced';
    this.mark(true);
  }

  /**
   * Records the failure of the operations it affects. Remote delivery that
   * merely retries keeps its state; only a terminal answer changes it.
   */
  fail(error: unknown): void {
    if (this.isTerminal()) return;
    const code = allowlisted(readField(error, 'code'), /^[A-Z_]{1,64}$/);
    const name = readField(error, 'name');
    const failureClass = readField(error, 'failureClass');
    const failureStage = allowlisted(
      readField(error, 'failureStage'),
      /^(prepare|read-schema|pull|normalize|plan|history|commit)$/
    );
    const failureName = allowlisted(
      readField(error, 'failureName'),
      /^[A-Za-z][\w.$:-]{0,127}$/
    );
    const failureMessage = readField(error, 'failureMessage');
    const sample = {
      ...(failureStage
        ? {
            failureStage,
            ...(failureName ? { failureName } : {}),
            ...(typeof failureMessage === 'string'
              ? { failureMessage: failureMessage.slice(0, 512) }
              : {}),
          }
        : {}),
      phase: this.snapshot.state === 'reading' ? 'import' : 'sync',
      code,
      failureClass: (DOC_SYNC_FAILURE_CLASSES as readonly unknown[]).includes(
        failureClass
      )
        ? (failureClass as DocSyncFailureClass)
        : null,
      timestamp: Date.now(),
    } as const;
    for (const operationId of this.affectedOperations(error))
      this.record({ ...sample, operationId });
    if (name === 'AbortError' || code === 'CANCELLED')
      this.snapshot.state = 'cancelled';
    else if (code && CONFLICT_CODES.has(code)) this.snapshot.state = 'conflict';
    this.mark(true);
  }

  private isTerminal() {
    return ['synced', 'conflict', 'cancelled'].includes(this.snapshot.state);
  }

  private affectedOperations(error: unknown): (string | null)[] {
    const named = allowlisted(
      readField(error, 'operationId'),
      /^[\w:.\-]{1,128}$/
    );
    if (named) return [named];
    const pending = this.receipts
      .filter(receipt =>
        receipt.clocks.some(clock => !this.syncedDocIds.has(clock.docId))
      )
      .map(receipt => allowlisted(receipt.operationId, /^[\w:.\-]{1,128}$/))
      .filter(operationId => operationId !== null);
    return pending.length ? [...new Set(pending)] : [null];
  }

  private record(sample: DatabaseImportFailureSample) {
    if (this.samples.length >= FAILURE_SAMPLE_LIMIT) return;
    this.samples.push(sample);
    try {
      performance.mark(DATABASE_IMPORT_FAILURE_MARK, { detail: sample });
    } catch {
      // Diagnostic availability must never change the import's result.
    }
  }

  private markNumbers(name: string, detail: Record<string, number>) {
    try {
      performance.mark(name, {
        detail: Object.fromEntries(
          Object.entries(detail).filter(([, value]) => Number.isFinite(value))
        ),
      });
    } catch {
      // Diagnostic availability must never change the import's result.
    }
  }

  private mark(stateChange: boolean) {
    if (!stateChange && this.rowMarks++ >= ROW_MARK_LIMIT) return;
    try {
      performance.mark(DATABASE_IMPORT_PROGRESS_MARK, {
        detail: { ...this.snapshot },
      });
    } catch {
      // Diagnostic availability must never change the import's result.
    }
  }
}
